/**
 * Admin Coupons console.
 *
 *   - List with search + status filter + redemption count
 *   - Create / edit modal
 *   - Bulk-generate (e.g. 50 random codes that all give 20% off, single-use)
 *   - Per-coupon detail drawer with redemption log
 *   - CSV export
 */
import { useEffect, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/helpers';
import { useCurrency } from '../../hooks/useCurrency';
import { Icon } from './AdminIcons';

export default function CouponsPage() {
  const { fmt } = useCurrency({ usd: true });
  const [coupons, setCoupons]       = useState([]);
  const [pagination, setPagination] = useState({});
  const [page, setPage]             = useState(1);
  const [status, setStatus]         = useState('');
  const [search, setSearch]         = useState('');
  const [editing, setEditing]       = useState(null);     // coupon object or 'new' or null
  const [bulkOpen, setBulkOpen]     = useState(false);
  const [detailId, setDetailId]     = useState(null);

  const load = () => {
    const params = { page };
    if (status) params.status = status;
    if (search) params.search = search;
    api.get('/admin/coupons', { params })
      .then(res => { setCoupons(res.data.data); setPagination(res.data.pagination); })
      .catch(() => toast.error('Failed to load coupons'));
  };
  useEffect(() => { load(); }, [page, status]);   // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const toggle = async (c, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/admin/coupons/${c.id}/toggle`);
      setCoupons(prev => prev.map(x => x.id === c.id ? data : x));
    } catch {
      toast.error('Toggle failed');
    }
  };

  const remove = async (c, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete coupon ${c.code}? Only unused coupons can be deleted.`)) return;
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      load();
      toast.success('Coupon deleted');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold inline-flex items-center gap-2"><Icon name="coupons" className="w-6 h-6 text-tea-500" />Coupon Codes</h2>
          <p className="text-sm text-gray-500 mt-1">Create codes, attach them to email blasts, track redemptions.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={async () => {
            try {
              const res = await api.get('/admin/coupons/export.csv', { responseType: 'blob' });
              const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
              const a = document.createElement('a');
              a.href = url; a.download = 'ab8beauty-coupons.csv';
              document.body.appendChild(a); a.click(); a.remove();
              URL.revokeObjectURL(url);
            } catch { toast.error('Export failed'); }
          }}
            className="text-sm font-semibold px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
            Export CSV
          </button>
          <button onClick={() => setBulkOpen(true)}
            className="text-sm font-semibold px-3 py-2 rounded-lg border border-tea-300 text-tea-700 hover:bg-tea-50 inline-flex items-center gap-1.5">
            <Icon name="sparkles" className="w-4 h-4" />Bulk generate
          </button>
          <button onClick={() => setEditing('new')}
            className="text-sm font-bold px-4 py-2 rounded-lg bg-tea-600 text-white hover:bg-tea-700">
            + New coupon
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border rounded-xl p-3 mb-4 flex gap-3 items-center shadow-sm flex-wrap">
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search code or description…"
          className="flex-1 min-w-[200px] text-sm border border-gray-300 rounded-lg px-3 py-1.5" />
        <select value={status} onChange={e => setStatus(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Disabled</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['Code', 'Discount', 'Description', 'Redeemed', 'Expires', 'Status', ''].map(h => (
                <th key={h} className="text-left px-3 py-2.5 font-semibold text-gray-600 text-xs uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-gray-400">No coupons yet. Click "New coupon" to make your first one.</td></tr>
            )}
            {coupons.map(c => {
              const expired = c.expiresAt && new Date(c.expiresAt) < new Date();
              return (
                <tr key={c.id} className="border-b last:border-0 hover:bg-tea-50 cursor-pointer"
                    onClick={() => setDetailId(c.id)}>
                  <td className="px-3 py-2.5 font-mono font-bold text-tea-700">{c.code}</td>
                  <td className="px-3 py-2.5">{discountLabel(c, fmt)}</td>
                  <td className="px-3 py-2.5 text-gray-600 truncate max-w-[18rem]">{c.description || '—'}</td>
                  <td className="px-3 py-2.5 tabular-nums">
                    {c.timesRedeemed}
                    {c.maxTotalRedemptions ? <span className="text-gray-400"> / {c.maxTotalRedemptions}</span> : ''}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">{c.expiresAt ? formatDate(c.expiresAt) : 'Never'}</td>
                  <td className="px-3 py-2.5">
                    <span className={'text-xs font-bold px-2 py-0.5 rounded-full ' +
                      (expired ? 'bg-gray-100 text-gray-500' :
                       c.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
                      {expired ? 'Expired' : (c.isActive ? 'Active' : 'Off')}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                    <button onClick={e => toggle(c, e)}
                      className="text-xs font-medium text-tea-600 hover:text-tea-700 mr-3">
                      {c.isActive ? 'Disable' : 'Enable'}
                    </button>
                    {c.timesRedeemed === 0 && (
                      <button onClick={e => remove(c, e)}
                        className="text-xs font-medium text-red-500 hover:text-red-700">
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="flex gap-1 mt-3 justify-end">
          {[...Array(pagination.pages)].map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={'px-3 py-1 text-xs border rounded ' +
                (page === i + 1 ? 'bg-tea-600 text-white border-tea-600' : 'hover:border-tea-400')}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {editing && (
        <EditModal
          coupon={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {bulkOpen && (
        <BulkGenerateModal
          onClose={() => setBulkOpen(false)}
          onCreated={() => { setBulkOpen(false); load(); }}
        />
      )}
      {detailId && (
        <DetailDrawer id={detailId} onClose={() => setDetailId(null)} onEdit={(c) => { setEditing(c); setDetailId(null); }} />
      )}
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────
function discountLabel(c, fmt) {
  if (c.discountType === 'percent') return <span className="font-bold text-tea-700">{c.discountValue}% off</span>;
  if (c.discountType === 'fixed_amount') return <span className="font-bold text-tea-700">{fmt(c.discountValue)} off</span>;
  if (c.discountType === 'free_shipping') return <span className="font-bold text-blue-700">Free shipping</span>;
  return c.discountType;
}

// ═══════════════════════════════════════════════════════════════════════════
//   CREATE / EDIT MODAL
// ═══════════════════════════════════════════════════════════════════════════

function EditModal({ coupon, onClose, onSaved }) {
  const isNew = !coupon;
  const [form, setForm] = useState({
    code: coupon?.code || '',
    description: coupon?.description || '',
    discountType: coupon?.discountType || 'percent',
    discountValue: coupon?.discountValue ?? 20,
    minSubtotalCents: coupon?.minSubtotalCents || 0,
    maxDiscountCents: coupon?.maxDiscountCents || '',
    maxTotalRedemptions: coupon?.maxTotalRedemptions ?? '',
    maxPerCustomer: coupon?.maxPerCustomer ?? 1,
    startsAt: coupon?.startsAt ? coupon.startsAt.slice(0, 10) : '',
    expiresAt: coupon?.expiresAt ? coupon.expiresAt.slice(0, 10) : '',
    isActive: coupon?.isActive ?? true,
    appliesToOneTime: coupon?.appliesToOneTime ?? true,
    appliesToSubscriptions: coupon?.appliesToSubscriptions ?? true,
    notes: coupon?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Convenience: dollar-cents conversion for fixed amount
  const valueInDollars = form.discountType === 'fixed_amount'
    ? (form.discountValue / 100).toFixed(2)
    : form.discountValue;
  const setValueFromInput = (v) => {
    if (form.discountType === 'fixed_amount') {
      set('discountValue', Math.round(parseFloat(v || '0') * 100));
    } else {
      set('discountValue', parseInt(v || '0', 10));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        code: form.code || undefined,
        startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
        maxTotalRedemptions: form.maxTotalRedemptions === '' ? null : parseInt(form.maxTotalRedemptions, 10),
        maxPerCustomer: form.maxPerCustomer === '' ? null : parseInt(form.maxPerCustomer, 10),
        maxDiscountCents: form.maxDiscountCents === '' ? null : parseInt(form.maxDiscountCents, 10),
        minSubtotalCents: parseInt(form.minSubtotalCents, 10) || 0,
      };
      if (isNew) {
        await api.post('/admin/coupons', payload);
        toast.success('Coupon created');
      } else {
        await api.put(`/admin/coupons/${coupon.id}`, payload);
        toast.success('Coupon updated');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.msg || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 py-8" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-bold inline-flex items-center gap-2">{isNew ? <><Icon name="coupons" className="w-5 h-5 text-tea-500" />New coupon</> : `Edit ${coupon.code}`}</h2>
        </div>

        <div className="px-6 py-5 space-y-4 overflow-y-auto">
          <Field label="Code" hint="Leave blank to auto-generate. Letters / numbers / _ - only.">
            <input value={form.code} onChange={e => set('code', e.target.value.toUpperCase())}
              placeholder="e.g. SUMMER20"
              className="w-full font-mono font-bold text-lg tracking-wider border border-gray-300 rounded-lg px-3 py-2" />
          </Field>

          <Field label="Description (internal)">
            <input value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="What's this code for?"
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Discount type">
              <select value={form.discountType} onChange={e => set('discountType', e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white">
                <option value="percent">% off</option>
                <option value="fixed_amount">$ off</option>
                <option value="free_shipping">Free shipping</option>
              </select>
            </Field>
            {form.discountType !== 'free_shipping' && (
              <Field label={form.discountType === 'percent' ? 'Percent off (1–100)' : 'Amount off (USD $)'}>
                <input type="number" min="0" max={form.discountType === 'percent' ? 100 : undefined}
                  step={form.discountType === 'percent' ? 1 : 0.01}
                  value={valueInDollars} onChange={e => setValueFromInput(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
              </Field>
            )}
          </div>

          {form.discountType === 'percent' && (
            <Field label="Cap on discount (USD $, optional)" hint="Useful for sitewide % codes — e.g. '20% off, max $50'.">
              <input type="number" min="0" step="0.01"
                value={form.maxDiscountCents === '' ? '' : (form.maxDiscountCents / 100)}
                onChange={e => set('maxDiscountCents', e.target.value === '' ? '' : Math.round(parseFloat(e.target.value) * 100))}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
            </Field>
          )}

          <Field label="Minimum subtotal to qualify (USD $)">
            <input type="number" min="0" step="0.01"
              value={(form.minSubtotalCents / 100) || 0}
              onChange={e => set('minSubtotalCents', Math.round(parseFloat(e.target.value || '0') * 100))}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Total redemptions" hint="Blank = unlimited">
              <input type="number" min="0" value={form.maxTotalRedemptions}
                onChange={e => set('maxTotalRedemptions', e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
            </Field>
            <Field label="Per-customer limit" hint="Blank = no limit. 1 = once each.">
              <input type="number" min="0" value={form.maxPerCustomer}
                onChange={e => set('maxPerCustomer', e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Starts on (optional)">
              <input type="date" value={form.startsAt} onChange={e => set('startsAt', e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
            </Field>
            <Field label="Expires on (optional)">
              <input type="date" value={form.expiresAt} onChange={e => set('expiresAt', e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
            </Field>
          </div>

          <div className="bg-gray-50 border rounded-lg p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} />
              <span>Active (customers can redeem)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.appliesToOneTime} onChange={e => set('appliesToOneTime', e.target.checked)} />
              <span>Applies to one-time orders</span>
            </label>
          </div>

          <Field label="Notes (internal, not shown to customer)">
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              rows={2}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
          </Field>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <button onClick={onClose}
            className="text-sm font-semibold px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="text-sm font-bold px-4 py-2 rounded-lg bg-tea-600 text-white hover:bg-tea-700 disabled:opacity-50">
            {saving ? 'Saving…' : (isNew ? 'Create coupon' : 'Save changes')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//   BULK GENERATE MODAL
// ═══════════════════════════════════════════════════════════════════════════

function BulkGenerateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    count: 25,
    prefix: 'VIP',
    discountType: 'percent',
    discountValue: 15,
    maxPerCustomer: 1,
    maxTotalRedemptions: 1,
    minSubtotalCents: 0,
    expiresAt: '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const generate = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        discountValue: form.discountType === 'fixed_amount'
          ? Math.round(parseFloat(form.discountValue || '0') * 100)
          : parseInt(form.discountValue, 10),
        minSubtotalCents: parseInt(form.minSubtotalCents, 10) || 0,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
      };
      const { data } = await api.post('/admin/coupons/bulk-generate', payload);
      toast.success(`Generated ${data.created} codes`);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Generate failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-bold inline-flex items-center gap-2"><Icon name="sparkles" className="w-5 h-5 text-tea-500" />Bulk generate codes</h2>
          <p className="text-xs text-gray-500 mt-1">Random unique codes that all share the same discount + limits.</p>
        </div>

        <div className="px-6 py-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="How many?">
              <input type="number" min="1" max="500" value={form.count}
                onChange={e => set('count', parseInt(e.target.value || '0', 10))}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
            </Field>
            <Field label="Prefix (optional)">
              <input value={form.prefix} onChange={e => set('prefix', e.target.value.toUpperCase())}
                placeholder="e.g. VIP"
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 font-mono uppercase" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select value={form.discountType} onChange={e => set('discountType', e.target.value)}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white">
                <option value="percent">% off</option>
                <option value="fixed_amount">$ off</option>
                <option value="free_shipping">Free shipping</option>
              </select>
            </Field>
            {form.discountType !== 'free_shipping' && (
              <Field label={form.discountType === 'percent' ? 'Percent off' : 'Amount off (USD $)'}>
                <input type="number" min="0" value={form.discountValue}
                  onChange={e => set('discountValue', e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
              </Field>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Per-customer limit">
              <input type="number" min="0" value={form.maxPerCustomer}
                onChange={e => set('maxPerCustomer', parseInt(e.target.value || '0', 10))}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
            </Field>
            <Field label="Total redemptions">
              <input type="number" min="0" value={form.maxTotalRedemptions}
                onChange={e => set('maxTotalRedemptions', parseInt(e.target.value || '0', 10))}
                className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
            </Field>
          </div>
          <Field label="Expires (optional)">
            <input type="date" value={form.expiresAt} onChange={e => set('expiresAt', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
          </Field>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <button onClick={onClose}
            className="text-sm font-semibold px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={generate} disabled={saving}
            className="text-sm font-bold px-4 py-2 rounded-lg bg-tea-600 text-white hover:bg-tea-700 disabled:opacity-50">
            {saving ? 'Generating…' : `Generate ${form.count} codes`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//   DETAIL DRAWER (redemption history)
// ═══════════════════════════════════════════════════════════════════════════

function DetailDrawer({ id, onClose, onEdit }) {
  const { fmt } = useCurrency({ usd: true });
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/coupons/${id}`)
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load coupon'));
  }, [id]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl h-full shadow-2xl flex flex-col">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-mono text-2xl font-bold text-tea-700">{data?.coupon.code || '…'}</h2>
            <p className="text-xs text-gray-500">{data?.coupon.description}</p>
          </div>
          <div className="flex gap-2 items-center">
            {data && (
              <button onClick={() => onEdit(data.coupon)}
                className="text-xs font-semibold px-3 py-1.5 rounded-md border border-tea-300 text-tea-700 hover:bg-tea-50">
                Edit
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
          </div>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-3 gap-2 px-6 py-4 border-b bg-gray-50">
              <Stat label="Discount" value={data.coupon.discountType === 'percent' ? `${data.coupon.discountValue}%` :
                data.coupon.discountType === 'fixed_amount' ? fmt(data.coupon.discountValue) : 'Free ship'} />
              <Stat label="Redemptions" value={data.stats.total_redemptions} />
              <Stat label="Discount given" value={fmt(data.stats.total_discount_given)} />
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="text-xs text-gray-500 uppercase font-bold mb-3 tracking-wider">Redemption log</div>
              {data.redemptions.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No redemptions yet.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b text-xs text-gray-500">
                    <tr>
                      <th className="text-left py-2">Customer</th>
                      <th className="text-left">Order</th>
                      <th className="text-right">Discount</th>
                      <th className="text-left">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.redemptions.map(r => (
                      <tr key={r.id} className="border-b last:border-0">
                        <td className="py-2">
                          {r.user ? (
                            <>
                              <div className="font-medium">{r.user.firstName} {r.user.lastName}</div>
                              <div className="text-xs text-gray-500">{r.user.email}</div>
                            </>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="text-xs font-mono text-gray-600">
                          {r.order ? `#${r.order.id.slice(0, 8).toUpperCase()}` :
                           r.subscriptionId ? <span className="text-purple-600">Subscription</span> : '—'}
                        </td>
                        <td className="text-right tabular-nums font-medium">{fmt(r.discountInCents)}</td>
                        <td className="text-xs text-gray-500">{formatDate(r.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded p-2 text-center bg-white border">
      <div className="text-lg font-bold text-gray-800">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}
